import { MGPFallible, MGPOptional, MGPValidation } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { GameStatus } from '../../jscaip/GameStatus';
import { HexaDirection } from '../../jscaip/HexaDirection';
import { Player } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { YinshFailure } from './YinshFailure';
import { YinshCapture } from './YinshMove';
import { YinshPiece } from './YinshPiece';
import { YinshState } from './YinshState';
export class YinshNode extends GameNode {
}
export class YinshRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (YinshRules.singleton.isAbsent()) {
            YinshRules.singleton = MGPOptional.of(new YinshRules());
        }
        return YinshRules.singleton.get();
    }
    getInitialState() {
        const _ = YinshPiece.EMPTY;
        const N = YinshPiece.UNREACHABLE;
        const board = [
            [N, N, N, N, N, N, _, _, _, _, N],
            [N, N, N, N, _, _, _, _, _, _, _],
            [N, N, N, _, _, _, _, _, _, _, _],
            [N, N, _, _, _, _, _, _, _, _, _],
            [N, _, _, _, _, _, _, _, _, _, _],
            [N, _, _, _, _, _, _, _, _, _, N],
            [_, _, _, _, _, _, _, _, _, _, N],
            [_, _, _, _, _, _, _, _, _, N, N],
            [_, _, _, _, _, _, _, _, N, N, N],
            [_, _, _, _, _, _, _, N, N, N, N],
            [N, _, _, _, _, N, N, N, N, N, N],
        ];
        return new YinshState(board, PlayerNumberMap.of(5, 5), 0);
    }
    applyLegalMove(_move, _state, _config, info) {
        const stateWithoutTurn = info;
        return new YinshState(stateWithoutTurn.board, stateWithoutTurn.sideRings, stateWithoutTurn.turn + 1);
    }
    applyCaptures(captures, state) {
        let computedState = state;
        captures.forEach((capture) => {
            computedState = this.applyCapture(capture, computedState);
        });
        return computedState;
    }
    applyCapture(capture, state) {
        const board = this.applyCaptureWithoutTakingRing(state, capture);
        return this.takeRing(new YinshState(board, state.sideRings, state.turn), capture.ringTaken.get());
    }
    takeRing(state, ringTaken) {
        const player = state.getCurrentPlayer();
        const board = state.setAt(ringTaken, YinshPiece.EMPTY).board;
        const sideRings = state.sideRings.getCopy();
        sideRings.add(player, 1);
        return new YinshState(board, sideRings, state.turn);
    }
    applyCaptureWithoutTakingRing(state, capture) {
        // Take all markers
        capture.forEach((coord) => {
            state = state.setAt(coord, YinshPiece.EMPTY);
        });
        return state.board;
    }
    ringSelectionValidity(state, coord) {
        const player = state.getCurrentPlayer();
        if (state.getPieceAt(coord) === YinshPiece.RINGS.get(player)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(YinshFailure.CAPTURE_SHOULD_TAKE_RING());
        }
    }
    applyRingMoveAndFlip(start, end, state) {
        const player = state.getCurrentPlayer();
        // Move ring from start (only the marker remains) to
        // end (only the ring can be there, as it must land on an empty space)
        let newState = state.setAt(start, YinshPiece.MARKERS.get(player));
        newState = newState.setAt(end, YinshPiece.RINGS.get(player));
        // Flip all pieces between start and end (both not included)
        // Direction is valid because this function is called with a valid move
        const dir = HexaDirection.factory.fromMove(start, end).get();
        for (let coord = start.getNext(dir); coord.equals(end) === false; coord = coord.getNext(dir)) {
            const piece = newState.getPieceAt(coord);
            if (piece !== YinshPiece.EMPTY) {
                newState = newState.setAt(coord, piece.flip());
            }
        }
        return newState;
    }
    isLegal(move, state) {
        if (move.isInitialPlacement()) {
            return this.initialPlacementValidity(state, move.start);
        }
        if (state.isInitialPlacementPhase()) {
            return MGPFallible.failure(YinshFailure.NO_MARKERS_IN_INITIAL_PHASE());
        }
        const initialCapturesValidity = this.capturesValidity(state, move.initialCaptures);
        if (initialCapturesValidity.isFailure()) {
            return initialCapturesValidity.toOtherFallible();
        }
        const stateAfterInitialCaptures = this.applyCaptures(move.initialCaptures, state);
        const moveValidity = this.moveValidity(stateAfterInitialCaptures, move.start, move.end.get());
        if (moveValidity.isFailure()) {
            return moveValidity.toOtherFallible();
        }
        const stateAfterRingMove = this.applyRingMoveAndFlip(move.start, move.end.get(), stateAfterInitialCaptures);
        const finalCapturesValidity = this.capturesValidity(stateAfterRingMove, move.finalCaptures);
        if (finalCapturesValidity.isFailure()) {
            return finalCapturesValidity.toOtherFallible();
        }
        const stateAfterFinalCaptures = this.applyCaptures(move.finalCaptures, stateAfterRingMove);
        const noMoreCapturesValidity = this.noMoreCapturesValidity(stateAfterFinalCaptures);
        if (noMoreCapturesValidity.isFailure()) {
            return noMoreCapturesValidity.toOtherFallible();
        }
        return MGPFallible.success(stateAfterFinalCaptures);
    }
    initialPlacementValidity(state, coord) {
        if (state.isInitialPlacementPhase() === false) {
            return MGPFallible.failure(YinshFailure.PLACEMENT_AFTER_INITIAL_PHASE());
        }
        if (state.getPieceAt(coord) !== YinshPiece.EMPTY) {
            return MGPFallible.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SPACE());
        }
        const player = state.getCurrentPlayer();
        const sideRings = state.sideRings.getCopy();
        sideRings.add(player, -1);
        const newBoard = state.setAt(coord, YinshPiece.of(player, true)).board;
        const newState = new YinshState(newBoard, sideRings, state.turn);
        return MGPFallible.success(newState);
    }
    moveStartValidity(state, start) {
        const player = state.getCurrentPlayer();
        // Start coord has to contain a ring of the current player
        if (state.getPieceAt(start) === YinshPiece.RINGS.get(player)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(YinshFailure.SHOULD_SELECT_PLAYER_RING());
        }
    }
    moveValidity(state, start, end) {
        const moveStartValidity = this.moveStartValidity(state, start);
        if (moveStartValidity.isFailure()) {
            return moveStartValidity;
        }
        // End coord has to be empty
        if (state.getPieceAt(end) !== YinshPiece.EMPTY) {
            return MGPValidation.failure(YinshFailure.SHOULD_END_MOVE_ON_EMPTY_SPACE());
        }
        // There should only be markers or empty spaces between start and end
        // As soon as a marker group is passed, the move should stop on the first empty space
        // There cannot be rings between start and end
        const directionOptional = HexaDirection.factory.fromMove(start, end);
        if (directionOptional.isFailure()) {
            return MGPValidation.failure(YinshFailure.MOVE_DIRECTION_INVALID());
        }
        const direction = directionOptional.get();
        let markersPassed = false;
        for (let cur = start.getNext(direction); cur.equals(end) === false; cur = cur.getNext(direction)) {
            const piece = state.getPieceAt(cur);
            if (piece === YinshPiece.EMPTY) {
                if (markersPassed) {
                    return MGPValidation.failure(YinshFailure.MOVE_SHOULD_END_AT_FIRST_EMPTY_SPACE_AFTER_MARKERS());
                }
            }
            else if (piece.isRing) {
                return MGPValidation.failure(YinshFailure.MOVE_SHOULD_NOT_PASS_ABOVE_RING());
            }
            else {
                // Piece is a marker
                markersPassed = true;
            }
        }
        return MGPValidation.SUCCESS;
    }
    capturesValidity(state, captures) {
        let updatedState = state;
        for (const capture of captures) {
            const validity = this.captureValidity(updatedState, capture);
            if (validity.isFailure()) {
                return validity;
            }
            updatedState = this.applyCapture(capture, updatedState);
        }
        return MGPValidation.SUCCESS;
    }
    captureValidity(state, capture) {
        const player = state.getCurrentPlayer();
        // There should be exactly 5 consecutive spaces, on the same line (invariants of YinshCapture)
        for (const coord of capture.capturedSpaces) {
            // The captured spaces must contain markers of the current player
            if (state.getPieceAt(coord) !== YinshPiece.MARKERS.get(player)) {
                return MGPValidation.failure(YinshFailure.CAN_ONLY_CAPTURE_YOUR_MARKERS());
            }
        }
        // The ring taken should be a ring
        if (state.getPieceAt(capture.ringTaken.get()) !== YinshPiece.RINGS.get(player)) {
            return MGPValidation.failure(YinshFailure.CAPTURE_SHOULD_TAKE_RING());
        }
        return MGPValidation.SUCCESS;
    }
    noMoreCapturesValidity(state) {
        const player = state.getCurrentPlayer();
        const linePortions = this.getLinePortionsWithAtLeastFivePiecesOfPlayer(state, player);
        if (linePortions.length === 0) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(YinshFailure.MISSING_CAPTURES());
        }
    }
    getLinePortionsWithAtLeastFivePiecesOfPlayer(state, player) {
        const linePortions = [];
        state.allLines().forEach((line) => {
            const linePortion = this.getLinePortionWithAtLeastFivePiecesOfPlayer(state, player, line);
            if (linePortion.isPresent()) {
                linePortions.push(linePortion.get());
            }
        });
        return linePortions;
    }
    getLinePortionWithAtLeastFivePiecesOfPlayer(state, player, line) {
        let consecutives = 0;
        const coord = state.getEntranceOnLine(line);
        const dir = line.getDirection();
        let start = coord;
        let cur;
        for (cur = coord; state.isOnBoard(cur); cur = cur.getNext(dir)) {
            const piece = state.getPieceAt(cur);
            if (piece.player === player && piece.isRing === false) {
                if (consecutives === 0) {
                    start = cur;
                }
                consecutives += 1;
            }
            else {
                if (5 <= consecutives) {
                    // There can only be one portion with at least 5 pieces, we found it
                    break;
                }
                consecutives = 0;
            }
        }
        if (5 <= consecutives) {
            return MGPOptional.of({ start, end: cur, dir });
        }
        return MGPOptional.empty();
    }
    getPossibleCaptures(state) {
        const player = state.getCurrentPlayer();
        const captures = [];
        this.getLinePortionsWithAtLeastFivePiecesOfPlayer(state, player)
            .forEach((linePortion) => {
            for (let cur = linePortion.start; 5 <= cur.getLinearDistanceToward(linePortion.end); cur = cur.getNext(linePortion.dir)) {
                captures.push(YinshCapture.of(cur, cur.getNext(linePortion.dir, 4)));
            }
        });
        return captures;
    }
    getRingTargets(state, start) {
        const targets = [];
        for (const dir of HexaDirection.factory.all) {
            let pieceSeen = false;
            for (let cur = start.getNext(dir); state.isOnBoard(cur); cur = cur.getNext(dir)) {
                const piece = state.getPieceAt(cur);
                if (piece === YinshPiece.EMPTY) {
                    targets.push(cur);
                    if (pieceSeen) {
                        // can only land directly after the piece group
                        break;
                    }
                }
                else if (piece.isRing) {
                    // cannot land on rings nor after them
                    break;
                }
                else {
                    // track whether we have seen pieces, as we can only jump above one group
                    pieceSeen = true;
                }
            }
        }
        return targets;
    }
    getGameStatus(node) {
        if (node.gameState.isInitialPlacementPhase()) {
            return GameStatus.ONGOING;
        }
        if (3 <= node.gameState.sideRings.get(Player.ZERO)) {
            return GameStatus.ZERO_WON;
        }
        if (3 <= node.gameState.sideRings.get(Player.ONE)) {
            return GameStatus.ONE_WON;
        }
        return GameStatus.ONGOING;
    }
}
